import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface PublishControlProps {
  pageKey: string;
  pageTitle: string;
}

export default function PublishControl({ pageKey, pageTitle }: PublishControlProps) {
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    fetchPublishStatus();
  }, [pageKey]);

  const fetchPublishStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .eq('key', `${pageKey}_published`)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        await createPublishSetting(true);
        setIsPublished(true);
      } else {
        setIsPublished(data.value === true || data.value === 'true' || data.value == null);
        setLastUpdated(data.updated_at || null);
      }
    } catch (err) {
      console.error(`Error fetching ${pageKey} publish status:`, err);
      setError(`Failed to load ${pageTitle} publish status`);
    } finally {
      setLoading(false);
    }
  };

  const createPublishSetting = async (value: boolean) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .insert([{
          key: `${pageKey}_published`,
          value: value,
          category: 'content',
          description: `Controls whether the ${pageTitle} page is publicly visible`,
          is_public: true
        }]);
      
      if (error) throw error;
      
      return true;
    } catch (err) {
      console.error(`Error creating ${pageKey} publish setting:`, err);
      return false;
    }
  };

  const togglePublishStatus = async () => {
    try {
      setUpdating(true);
      setError(null);
      
      // Check if setting exists
      const { data, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', `${pageKey}_published`)
        .maybeSingle();
      
      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }
      
      const newStatus = !isPublished;
      
      if (data) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ 
            value: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', data.id);
          
        if (updateError) throw updateError;
      } else {
        // Create new setting
        const success = await createPublishSetting(newStatus);
        if (!success) {
          throw new Error(`Failed to create ${pageTitle} publish setting`);
        }
      }
      
      setIsPublished(newStatus);
      setLastUpdated(new Date().toISOString());
      toast.success(`${pageTitle} ${newStatus ? 'published' : 'unpublished'} successfully`);
      // Force reload to update navigation
      window.location.reload();
    } catch (err) {
      console.error(`Error toggling ${pageKey} publish status:`, err);
      setError(`Failed to update ${pageTitle} publish status`);
      toast.error(`Failed to update ${pageTitle} publish status`);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-golf-gray">{pageTitle} Publication Status</h2>
          <button
            onClick={fetchPublishStatus}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
            title="Refresh status"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 text-red-600 mb-6">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${isPublished ? 'bg-green-100' : 'bg-yellow-100'}`}>
                {isPublished ? (
                  <EyeIcon className="w-6 h-6 text-green-600" />
                ) : (
                  <EyeSlashIcon className="w-6 h-6 text-yellow-600" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-medium text-golf-gray">
                  {isPublished ? 'Published' : 'Unpublished'}
                </h3>
                <p className="text-sm text-gray-500">
                  {isPublished 
                    ? `The ${pageTitle} is currently visible to all users` 
                    : `The ${pageTitle} is currently only visible to administrators`}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: {formatDate(lastUpdated)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={togglePublishStatus}
            disabled={updating || loading}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium ${
              isPublished 
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            } transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {updating ? (
              <>
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                <span>Updating...</span>
              </>
            ) : isPublished ? (
              <>
                <EyeSlashIcon className="w-5 h-5" />
                <span>Unpublish {pageTitle}</span>
              </>
            ) : (
              <>
                <EyeIcon className="w-5 h-5" />
                <span>Publish {pageTitle}</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <button
          onClick={() => setInfoOpen(o => !o)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <h2 className="text-base font-semibold text-golf-gray">About {pageTitle} Publication</h2>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${infoOpen ? 'rotate-180' : ''}`} />
        </button>

        {infoOpen && (
          <div className="px-6 pb-6 prose prose-sm max-w-none border-t border-gray-100 pt-4">
            <p>
              The {pageTitle} publication status controls whether the {pageTitle.toLowerCase()} is visible to the public.
              When published, all visitors can view the {pageTitle.toLowerCase()}. When unpublished, only administrators
              can see the {pageTitle.toLowerCase()}.
            </p>

            <h3>When to publish:</h3>
            <ul>
              <li>When the {pageTitle.toLowerCase()} content is ready for public viewing</li>
              <li>When you want to make the {pageTitle.toLowerCase()} accessible to all visitors</li>
            </ul>

            <h3>When to unpublish:</h3>
            <ul>
              <li>When the {pageTitle.toLowerCase()} is under construction or being updated</li>
              <li>When you want to temporarily hide the {pageTitle.toLowerCase()} from public view</li>
              <li>When the {pageTitle.toLowerCase()} contains information that is not yet ready for release</li>
            </ul>

            <div className="bg-blue-50 p-4 rounded-lg mt-4">
              <h4 className="text-blue-800 font-medium flex items-center gap-2 mb-2">
                <CheckCircleIcon className="w-5 h-5" />
                <span>Tip</span>
              </h4>
              <p className="text-blue-700 mb-0">
                Even when unpublished, administrators can still view and test the {pageTitle.toLowerCase()} page.
                This allows you to prepare and verify the content before making it public.
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}